import { c as _c } from "react/compiler-runtime";
import { differenceInDays } from 'date-fns';
import { useContext, useState } from 'react';
import { Route } from 'react-router-dom';
import useSWR from 'swr';
import APIMethodKeys from '../../../../../../client/APIMethodKeys';
import { capitalizeFirstLetter } from '../../../../../../utils';
import Alert from '../../../../../common/base/Alert';
import Badge from '../../../../../common/base/Badge';
import Button from '../../../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../../../common/base/Card';
import Container from '../../../../../common/base/Container';
import Divider from '../../../../../common/base/Divider';
import Icon from '../../../../../common/base/Icon';
import Loading from '../../../../../common/base/Loading';
import Text from '../../../../../common/base/Text';
import Tooltip from '../../../../../common/base/Tooltip';
import { Div } from '../../../../../common/helpers/StyledUtils';
import { GlobalContext } from '../../../../../contexts/GlobalContext';
import { useAuthorized } from '../../../../../contexts/TeamPermissionContext';
import { DashboardContext } from '../../../DashboardContext';
import Addons from './Addons';
import BillingAddressModal from './BillingAddressModal';
import BillingAlerts from './BillingAlerts';
import EmailModal from './EmailModal';
import { InvoicesTable } from './InvoicesTable';
import EventsUsage from './MeteredUsage';
import PaymentMethodModal from './PaymentMethodModal';
import ProjectThroughputs from './ProjectsThroughput';
import { OrbSubscription } from '../../../../../../typings/Orb.interface';
import Tabs from '../../../../../common/base/Tabs';
import { formatDate } from '../../../../../../utils/date';
import { useDatePreferences } from '../../../../../hooks/useDatePreferences';
const getDisplayPrice = (subscription_details: OrbSubscription) => {
  return subscription_details?.plan.prices.reduce((total, price) => {
    if (price.price_type === 'fixed_price') {
      let amount = Number(price.unit_config?.unit_amount) * price.fixed_price_quantity;
      if (subscription_details.plan.discount?.applies_to_price_ids.includes(price.id)) {
        let discount = 0;
        if (subscription_details.plan.discount.discount_type === 'amount') {
          discount = Number(subscription_details.plan.discount.amount_discount);
        } else if (subscription_details.plan.discount.discount_type === 'percentage') {
          discount = amount * subscription_details.plan.discount.percentage_discount;
        }
        amount = amount - discount;
      }
      return total + amount;
    }
    return total;
  }, 0);
};
const OrganizationBilling: React.FC = () => {
  const $ = _c(179);
  const {
    timezone
  } = useDatePreferences();
  const isAuthorized = useAuthorized("admin");
  const {
    subscription
  } = useContext(DashboardContext);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const [active_tab, setActiveTab] = useState("event_gateway");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = APIMethodKeys.billing.getCard();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== HookdeckAPI.billing) {
    t1 = () => HookdeckAPI.billing.getCard();
    $[1] = HookdeckAPI.billing;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: card
  } = useSWR(t0, t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = APIMethodKeys.billing.getSubscriptionDetails();
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== HookdeckAPI.billing) {
    t3 = () => HookdeckAPI.billing.getSubscriptionDetails();
    $[4] = HookdeckAPI.billing;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data: subscription_details
  } = useSWR(t2, t3);
  let t4;
  if ($[6] !== subscription?.outpost_plan) {
    t4 = subscription?.outpost_plan ? APIMethodKeys.billing.getOutpostSubscriptionDetails() : undefined;
    $[6] = subscription?.outpost_plan;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== HookdeckAPI.billing) {
    t5 = () => HookdeckAPI.billing.getOutpostSubscriptionDetails();
    $[8] = HookdeckAPI.billing;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    data: outpost_subscription_details
  } = useSWR(t4, t5);
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = APIMethodKeys.billing.getInvoices();
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== HookdeckAPI.billing) {
    t7 = () => HookdeckAPI.billing.getInvoices();
    $[11] = HookdeckAPI.billing;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const {
    data: invoices,
    mutate: mutateInvoices
  } = useSWR(t6, t7);
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = APIMethodKeys.billing.getBillingEmail();
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== HookdeckAPI.billing) {
    t9 = () => HookdeckAPI.billing.getBillingEmail();
    $[14] = HookdeckAPI.billing;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const {
    data: billing_email,
    mutate: mutateBillingEmail
  } = useSWR(t8, t9);
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = APIMethodKeys.billing.getBillingAddress();
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== HookdeckAPI.billing) {
    t11 = () => HookdeckAPI.billing.getBillingAddress();
    $[17] = HookdeckAPI.billing;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const {
    data: billing_address_response,
    mutate: mutateBillingAddress
  } = useSWR(t10, t11);
  if (subscription_details === undefined || billing_email === undefined || billing_address_response === undefined || subscription.outpost_plan && outpost_subscription_details === undefined) {
    let t12;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <Div p={8} flex={{
        justify: "center"
      }}><Loading /></Div>;
      $[19] = t12;
    } else {
      t12 = $[19];
    }
    return t12;
  }
  const billing_email_unset = !billing_email?.email || billing_email?.email === "placeholder@hookdeck.com";
  let t12;
  if ($[20] !== card) {
    t12 = card ? `${capitalizeFirstLetter(card.brand)} ****${card.last4}` : null;
    $[20] = card;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const card_label = t12;
  const billing_address = billing_address_response?.billing_address;
  const billing_address_unset = !billing_address?.line1;
  const tax_id = billing_address_response?.tax_id;
  let t13;
  if ($[22] !== billing_address) {
    const formatBillingAddress = () => {
      if (!billing_address || !billing_address.line1) {
        return;
      }
      const parts = [];
      if (billing_address.line1) {
        parts.push(billing_address.line1);
      }
      if (billing_address.line2) {
        parts.push(billing_address.line2);
      }
      if (billing_address.city) {
        parts.push(billing_address.city);
      }
      if (billing_address.postal_code) {
        parts.push(billing_address.postal_code);
      }
      if (billing_address.country) {
        parts.push(billing_address.country);
      }
      return parts.join(", ");
    };
    t13 = formatBillingAddress();
    $[22] = billing_address;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const billing_address_formatted = t13;
  let t14;
  if ($[24] !== billing_email || $[25] !== isAuthorized || $[26] !== mutateBillingEmail) {
    t14 = <Route path="/settings/organization/billing/email" render={() => isAuthorized && <EmailModal email={billing_email.email} mutate={email => mutateBillingEmail({
      email
    })} />} />;
    $[24] = billing_email;
    $[25] = isAuthorized;
    $[26] = mutateBillingEmail;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const t15 = isAuthorized ? PaymentMethodModal : undefined;
  let t16;
  if ($[28] !== t15) {
    t16 = <Route path="/settings/organization/billing/payment-method" component={t15} />;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== billing_address || $[31] !== isAuthorized || $[32] !== mutateBillingAddress || $[33] !== tax_id) {
    t17 = <Route path="/settings/organization/billing/address" render={() => isAuthorized && <BillingAddressModal billing_address={billing_address ?? null} tax_id={tax_id ?? null} mutate={(address, new_tax_id) => mutateBillingAddress({
      billing_address: address,
      tax_id: new_tax_id
    })} />} />;
    $[30] = billing_address;
    $[31] = isAuthorized;
    $[32] = mutateBillingAddress;
    $[33] = tax_id;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      b: 10
    };
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Div flex={{
      justify: "space-between",
      align: "center"
    }} m={{
      b: 4
    }}><Text bold={true} size="l" as="h2" m={0}>Billing</Text></Div>;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      columns: 2,
      gap: 6
    };
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  let t22;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      x: 4,
      y: 3
    };
    t22 = <Text semi={true} size="xs">Billing period</Text>;
    $[38] = t21;
    $[39] = t22;
  } else {
    t21 = $[38];
    t22 = $[39];
  }
  let t23;
  if ($[40] !== subscription_details.current_billing_period_start_date || $[41] !== timezone) {
    t23 = formatDate(subscription_details.current_billing_period_start_date, "MMM d", timezone);
    $[40] = subscription_details.current_billing_period_start_date;
    $[41] = timezone;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] !== subscription_details.current_billing_period_end_date || $[44] !== timezone) {
    t24 = formatDate(subscription_details.current_billing_period_end_date, "MMM d", timezone);
    $[43] = subscription_details.current_billing_period_end_date;
    $[44] = timezone;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] !== t23 || $[47] !== t24) {
    t25 = <Div><StyledCard p={t21}>{t22}<Text base={true} size="m">{t23}{" "}{"->"}{" "}{t24}</Text></StyledCard></Div>;
    $[46] = t23;
    $[47] = t24;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = {
      x: 4,
      y: 3
    };
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = {
      justify: "space-between",
      align: "center"
    };
    $[50] = t27;
  } else {
    t27 = $[50];
  }
  let t28;
  let t29;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = {
      overflow: "hidden"
    };
    t29 = <Text semi={true} size="xs">Billing email</Text>;
    $[51] = t28;
    $[52] = t29;
  } else {
    t28 = $[51];
    t29 = $[52];
  }
  let t30;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = {
      maxWidth: "200px"
    };
    $[53] = t30;
  } else {
    t30 = $[53];
  }
  const t31 = billing_email_unset ? "Add billing email" : billing_email.email;
  let t32;
  if ($[54] !== billing_email_unset || $[55] !== t31) {
    t32 = <Div style={t28}>{t29}<Text muted={billing_email_unset} ellipsis={true} style={t30}>{t31}</Text></Div>;
    $[54] = billing_email_unset;
    $[55] = t31;
    $[56] = t32;
  } else {
    t32 = $[56];
  }
  const t33 = billing_email_unset ? "add" : "edit";
  let t34;
  if ($[57] !== t33) {
    t34 = <Button.Permission role="admin" ghost={true} icon={t33} to={_temp} />;
    $[57] = t33;
    $[58] = t34;
  } else {
    t34 = $[58];
  }
  let t35;
  if ($[59] !== t32 || $[60] !== t34) {
    t35 = <Div><StyledCard p={t26}><Div w={100} flex={t27}>{t32}{t34}</Div></StyledCard></Div>;
    $[59] = t32;
    $[60] = t34;
    $[61] = t35;
  } else {
    t35 = $[61];
  }
  let t36;
  if ($[62] !== t25 || $[63] !== t35) {
    t36 = <Div grid={t20}>{t25}{t35}</Div>;
    $[62] = t25;
    $[63] = t35;
    $[64] = t36;
  } else {
    t36 = $[64];
  }
  let t37;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <Div m={{
      t: 6
    }} />;
    $[65] = t37;
  } else {
    t37 = $[65];
  }
  let t38;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = {
      columns: 2,
      gap: 6
    };
    $[66] = t38;
  } else {
    t38 = $[66];
  }
  let t39;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = {
      x: 4,
      y: 3
    };
    $[67] = t39;
  } else {
    t39 = $[67];
  }
  let t40;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = {
      justify: "space-between",
      align: "center"
    };
    $[68] = t40;
  } else {
    t40 = $[68];
  }
  let t41;
  let t42;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = {
      overflow: "hidden"
    };
    t42 = <Text semi={true} ellipsis={true} size="xs">Payment method</Text>;
    $[69] = t41;
    $[70] = t42;
  } else {
    t41 = $[69];
    t42 = $[70];
  }
  let t43;
  if ($[71] !== card_label) {
    t43 = <Div style={t41}>{t42}<Text base={true} size="m" ellipsis={true}>{card_label ? <Text size="s" ellipsis={true}>{card_label}</Text> : <Text muted={true}>Add payment method</Text>}</Text></Div>;
    $[71] = card_label;
    $[72] = t43;
  } else {
    t43 = $[72];
  }
  const t44 = card ? "edit" : "add";
  let t45;
  if ($[73] !== t44) {
    t45 = <Button.Permission role="admin" ghost={true} icon={t44} to={_temp2} />;
    $[73] = t44;
    $[74] = t45;
  } else {
    t45 = $[74];
  }
  let t46;
  if ($[75] !== t43 || $[76] !== t45) {
    t46 = <Div><StyledCard p={t39}><Div flex={t40}>{t43}{t45}</Div></StyledCard></Div>;
    $[75] = t43;
    $[76] = t45;
    $[77] = t46;
  } else {
    t46 = $[77];
  }
  let t47;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = {
      x: 4,
      y: 3
    };
    $[78] = t47;
  } else {
    t47 = $[78];
  }
  let t48;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = {
      justify: "space-between",
      align: "center"
    };
    $[79] = t48;
  } else {
    t48 = $[79];
  }
  let t49;
  let t50;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = {
      overflow: "hidden"
    };
    t50 = <Text semi={true} ellipsis={true} size="xs">Billing address and Tax ID</Text>;
    $[80] = t49;
    $[81] = t50;
  } else {
    t49 = $[80];
    t50 = $[81];
  }
  let t51;
  if ($[82] !== billing_address_formatted || $[83] !== billing_address_unset) {
    t51 = <Div style={t49}>{t50}<Text base={true} size="m" ellipsis={true}>{billing_address_unset ? <Text muted={true}>Add billing address</Text> : <Text size="s" ellipsis={true} title={billing_address_formatted}>{billing_address_formatted}</Text>}</Text></Div>;
    $[82] = billing_address_formatted;
    $[83] = billing_address_unset;
    $[84] = t51;
  } else {
    t51 = $[84];
  }
  const t52 = billing_address_unset ? "add" : "edit";
  let t53;
  if ($[85] !== t52) {
    t53 = <Button.Permission role="admin" ghost={true} icon={t52} to={_temp3} />;
    $[85] = t52;
    $[86] = t53;
  } else {
    t53 = $[86];
  }
  let t54;
  if ($[87] !== t51 || $[88] !== t53) {
    t54 = <Div><StyledCard p={t47}><Div flex={t48}>{t51}{t53}</Div></StyledCard></Div>;
    $[87] = t51;
    $[88] = t53;
    $[89] = t54;
  } else {
    t54 = $[89];
  }
  let t55;
  if ($[90] !== t46 || $[91] !== t54) {
    t55 = <Div grid={t38}>{t46}{t54}</Div>;
    $[90] = t46;
    $[91] = t54;
    $[92] = t55;
  } else {
    t55 = $[92];
  }
  let t56;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = <Div m={{
      t: 6
    }} />;
    $[93] = t56;
  } else {
    t56 = $[93];
  }
  let t57;
  if ($[94] !== t36 || $[95] !== t55) {
    t57 = <Div m={t18}>{t19}{t36}{t37}{t55}{t56}</Div>;
    $[94] = t36;
    $[95] = t55;
    $[96] = t57;
  } else {
    t57 = $[96];
  }
  let t58;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = {
      b: 10
    };
    $[97] = t58;
  } else {
    t58 = $[97];
  }
  let t59;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = <Text bold={true} size="l" as="h2" m={{
      t: 0,
      b: 4
    }}>Invoices</Text>;
    $[98] = t59;
  } else {
    t59 = $[98];
  }
  let t60;
  if ($[99] !== mutateInvoices) {
    t60 = async () => {
      await mutateInvoices();
    };
    $[99] = mutateInvoices;
    $[100] = t60;
  } else {
    t60 = $[100];
  }
  let t61;
  if ($[101] !== invoices || $[102] !== t60) {
    t61 = <Div m={t58}>{t59}<InvoicesTable invoices={invoices} onRetried={t60} /></Div>;
    $[101] = invoices;
    $[102] = t60;
    $[103] = t61;
  } else {
    t61 = $[103];
  }
  let t62;
  if ($[104] !== t14 || $[105] !== t16 || $[106] !== t17 || $[107] !== t57 || $[108] !== t61) {
    t62 = <Container large={true} left={true}>{t14}{t16}{t17}{t57}{t61}</Container>;
    $[104] = t14;
    $[105] = t16;
    $[106] = t17;
    $[107] = t57;
    $[108] = t61;
    $[109] = t62;
  } else {
    t62 = $[109];
  }
  let t63;
  if ($[110] === Symbol.for("react.memo_cache_sentinel")) {
    t63 = <Div p={{
      x: 6
    }} m={{
      b: 8
    }}><Divider /></Div>;
    $[110] = t63;
  } else {
    t63 = $[110];
  }
  let t64;
  if ($[111] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = {
      b: 10
    };
    $[111] = t64;
  } else {
    t64 = $[111];
  }
  let t65;
  if ($[112] === Symbol.for("react.memo_cache_sentinel")) {
    t65 = <Div flex={{
      justify: "space-between",
      align: "center"
    }} m={{
      b: 4
    }}><Text bold={true} size="l" as="h2" m={0}>Plans</Text></Div>;
    $[112] = t65;
  } else {
    t65 = $[112];
  }
  let t66;
  if ($[113] === Symbol.for("react.memo_cache_sentinel")) {
    t66 = {
      b: 4
    };
    $[113] = t66;
  } else {
    t66 = $[113];
  }
  let t67;
  if ($[114] === Symbol.for("react.memo_cache_sentinel")) {
    t67 = {
      columns: 5,
      gap: 6
    };
    $[114] = t67;
  } else {
    t67 = $[114];
  }
  let t68;
  if ($[115] === Symbol.for("react.memo_cache_sentinel")) {
    t68 = {
      justify: "space-between"
    };
    $[115] = t68;
  } else {
    t68 = $[115];
  }
  let t69;
  if ($[116] === Symbol.for("react.memo_cache_sentinel")) {
    t69 = <Div flex={t68}><Text muted={true} semi={true} size="xs" p={{
        x: 4,
        y: 1
      }}>Product</Text></Div>;
    $[116] = t69;
  } else {
    t69 = $[116];
  }
  let t70;
  if ($[117] === Symbol.for("react.memo_cache_sentinel")) {
    t70 = {
      justify: "space-between"
    };
    $[117] = t70;
  } else {
    t70 = $[117];
  }
  let t71;
  if ($[118] === Symbol.for("react.memo_cache_sentinel")) {
    t71 = <Div flex={t70}><Text muted={true} semi={true} size="xs" p={{
        x: 4,
        y: 1
      }}>Current plan</Text></Div>;
    $[118] = t71;
  } else {
    t71 = $[118];
  }
  let t72;
  if ($[119] === Symbol.for("react.memo_cache_sentinel")) {
    t72 = <Div><Text muted={true} semi={true} size="xs" p={{
        x: 4,
        y: 1
      }}>Retention days</Text></Div>;
    $[119] = t72;
  } else {
    t72 = $[119];
  }
  let t73;
  if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
    t73 = {
      justify: "flex-end"
    };
    $[120] = t73;
  } else {
    t73 = $[120];
  }
  let t74;
  if ($[121] === Symbol.for("react.memo_cache_sentinel")) {
    t74 = <StyledCardSection muted={true}><Div grid={t67}>{t69}{t71}{t72}<Div flex={t73}><Text muted={true} semi={true} size="xs" p={{
            x: 4,
            y: 1
          }}>Base plan price</Text></Div></Div></StyledCardSection>;
    $[121] = t74;
  } else {
    t74 = $[121];
  }
  let t75;
  if ($[122] === Symbol.for("react.memo_cache_sentinel")) {
    t75 = {
      columns: 5,
      gap: 6
    };
    $[122] = t75;
  } else {
    t75 = $[122];
  }
  let t76;
  if ($[123] === Symbol.for("react.memo_cache_sentinel")) {
    t76 = {
      justify: "space-between"
    };
    $[123] = t76;
  } else {
    t76 = $[123];
  }
  let t77;
  if ($[124] === Symbol.for("react.memo_cache_sentinel")) {
    t77 = <Div flex={t76}><Div p={{
        x: 4,
        y: 3
      }} flex={{
        align: "center",
        gap: 2
      }}><Text capitalize={true} semi={true} size="s">Event Gateway</Text></Div></Div>;
    $[124] = t77;
  } else {
    t77 = $[124];
  }
  let t78;
  if ($[125] === Symbol.for("react.memo_cache_sentinel")) {
    t78 = {
      justify: "space-between"
    };
    $[125] = t78;
  } else {
    t78 = $[125];
  }
  let t79;
  let t80;
  if ($[126] === Symbol.for("react.memo_cache_sentinel")) {
    t79 = {
      x: 4,
      y: 3
    };
    t80 = {
      align: "center",
      gap: 2
    };
    $[126] = t79;
    $[127] = t80;
  } else {
    t79 = $[126];
    t80 = $[127];
  }
  let t81;
  if ($[128] !== subscription_details.plan.name) {
    t81 = <Div flex={t78}><Div p={t79} flex={t80}><Text capitalize={true} semi={true} size="s">{subscription_details.plan.name}</Text></Div></Div>;
    $[128] = subscription_details.plan.name;
    $[129] = t81;
  } else {
    t81 = $[129];
  }
  let t82;
  if ($[130] === Symbol.for("react.memo_cache_sentinel")) {
    t82 = {
      x: 4,
      y: 3
    };
    $[130] = t82;
  } else {
    t82 = $[130];
  }
  const t83 = subscription?.retention_days;
  let t84;
  if ($[131] !== t83) {
    t84 = <Div p={t82}><Badge neutral={true}>{t83} days</Badge></Div>;
    $[131] = t83;
    $[132] = t84;
  } else {
    t84 = $[132];
  }
  let t85;
  let t86;
  if ($[133] === Symbol.for("react.memo_cache_sentinel")) {
    t85 = {
      x: 4,
      y: 3
    };
    t86 = {
      justify: "flex-end",
      align: "center",
      gap: 2
    };
    $[133] = t85;
    $[134] = t86;
  } else {
    t85 = $[133];
    t86 = $[134];
  }
  let t87;
  if ($[135] !== subscription_details) {
    t87 = getDisplayPrice(subscription_details);
    $[135] = subscription_details;
    $[136] = t87;
  } else {
    t87 = $[136];
  }
  let t88;
  if ($[137] !== t87) {
    t88 = <Text size="s">${t87}</Text>;
    $[137] = t87;
    $[138] = t88;
  } else {
    t88 = $[138];
  }
  let t89;
  if ($[139] === Symbol.for("react.memo_cache_sentinel")) {
    t89 = <Tooltip tooltip="This amount will be charged at the start of your next billing cycle, after pro-rata adjustments, credits, and discounts."><Icon muted={true} small={true} pointer={true} icon="info" /></Tooltip>;
    $[139] = t89;
  } else {
    t89 = $[139];
  }
  let t90;
  if ($[140] !== t88) {
    t90 = <Div p={t85} flex={t86}>{t88}{t89}</Div>;
    $[140] = t88;
    $[141] = t90;
  } else {
    t90 = $[141];
  }
  let t91;
  if ($[142] === Symbol.for("react.memo_cache_sentinel")) {
    t91 = <Div p={{
      x: 2,
      y: 2
    }} flex={{
      justify: "flex-end",
      align: "center"
    }}><Button outline={true} to={_temp4}>Change</Button></Div>;
    $[142] = t91;
  } else {
    t91 = $[142];
  }
  let t92;
  if ($[143] !== t81 || $[144] !== t84 || $[145] !== t90) {
    t92 = <Div grid={t75}>{t77}{t81}{t84}{t90}{t91}</Div>;
    $[143] = t81;
    $[144] = t84;
    $[145] = t90;
    $[146] = t92;
  } else {
    t92 = $[146];
  }
  let t93;
  if ($[147] !== subscription) {
    t93 = subscription?.cancel_at && new Date(subscription.cancel_at).getTime() > Date.now() && <Alert warning={true} inline={true} m={{
      b: 4,
      x: 4
    }}>You have {differenceInDays(new Date(subscription.cancel_at), new Date())} days left to your plan. Afterward your plan will be downgraded to the Developer (free) Plan.</Alert>;
    $[147] = subscription;
    $[148] = t93;
  } else {
    t93 = $[148];
  }
  let t94;
  if ($[149] !== t92 || $[150] !== t93) {
    t94 = <StyledCardSection>{t92}{t93}</StyledCardSection>;
    $[149] = t92;
    $[150] = t93;
    $[151] = t94;
  } else {
    t94 = $[151];
  }
  let t95;
  if ($[152] !== outpost_subscription_details || $[153] !== subscription) {
    t95 = subscription?.outpost_plan && <StyledCardSection><Div grid={{
        columns: 5,
        gap: 6
      }}><Div flex={{
          justify: "space-between"
        }}><Div p={{
            x: 4,
            y: 3
          }} flex={{
            align: "center",
            gap: 2
          }}><Text capitalize={true} semi={true} size="s">Outpost</Text></Div></Div><Div flex={{
          justify: "space-between"
        }}><Div p={{
            x: 4,
            y: 3
          }} flex={{
            align: "center",
            gap: 2
          }}><Text capitalize={true} semi={true} size="s">{outpost_subscription_details.plan.name.replace("Outpost ", "").trim()}</Text></Div></Div><Div p={{
          x: 4,
          y: 3
        }}><Badge neutral={true}>{subscription?.outpost_config?.retention_days} days</Badge></Div><Div p={{
          x: 4,
          y: 3
        }} flex={{
          justify: "flex-end",
          align: "center",
          gap: 2
        }}><Text size="s">${getDisplayPrice(outpost_subscription_details)}</Text><Tooltip tooltip="This amount will be charged at the start of your next billing cycle, after pro-rata adjustments, credits, and discounts."><Icon muted={true} small={true} pointer={true} icon="info" /></Tooltip></Div><Div p={{
          x: 2,
          y: 2
        }} flex={{
          justify: "flex-end",
          align: "center"
        }}><Button outline={true} to={_temp5}>Change</Button></Div></Div>{subscription?.outpost_cancel_at && new Date(subscription.outpost_cancel_at).getTime() > Date.now() && <Alert warning={true} inline={true} m={{
        b: 4,
        x: 4
      }}>You have{" "}{differenceInDays(new Date(subscription.outpost_cancel_at), new Date())} days left to your plan. Afterward your plan will be downgraded to the Starter (free) Plan.</Alert>}</StyledCardSection>;
    $[152] = outpost_subscription_details;
    $[153] = subscription;
    $[154] = t95;
  } else {
    t95 = $[154];
  }
  let t96;
  if ($[155] !== t94 || $[156] !== t95) {
    t96 = <Div m={t64}>{t65}<StyledCard m={t66}>{t74}{t94}{t95}</StyledCard></Div>;
    $[155] = t94;
    $[156] = t95;
    $[157] = t96;
  } else {
    t96 = $[157];
  }
  let t97;
  if ($[158] === Symbol.for("react.memo_cache_sentinel")) {
    t97 = <Div m={{
      b: 10
    }}><Addons /></Div>;
    $[158] = t97;
  } else {
    t97 = $[158];
  }
  let t98;
  if ($[159] !== active_tab || $[160] !== subscription?.outpost_plan) {
    t98 = subscription?.outpost_plan && <Tabs m={{
      b: 10
    }} tabs={[{
      label: "Event Gateway",
      key: "event_gateway"
    }, {
      label: "Outpost",
      key: "outpost"
    }]} active_tab={active_tab} onTabSelected={tab => setActiveTab(tab as 'event_gateway' | 'outpost')} />;
    $[159] = active_tab;
    $[160] = subscription?.outpost_plan;
    $[161] = t98;
  } else {
    t98 = $[161];
  }
  let t99;
  if ($[162] !== active_tab || $[163] !== subscription_details) {
    t99 = active_tab === "event_gateway" && <Div m={{
      b: 10
    }}><ProjectThroughputs subscription_details={subscription_details} /></Div>;
    $[162] = active_tab;
    $[163] = subscription_details;
    $[164] = t99;
  } else {
    t99 = $[164];
  }
  let t100;
  if ($[165] !== active_tab) {
    t100 = <BillingAlerts key={active_tab} product={active_tab} />;
    $[165] = active_tab;
    $[166] = t100;
  } else {
    t100 = $[166];
  }
  const t101 = active_tab === "event_gateway" ? subscription_details : outpost_subscription_details;
  let t102;
  if ($[167] !== active_tab || $[168] !== t101) {
    t102 = <EventsUsage product={active_tab} subscription_details={t101} />;
    $[167] = active_tab;
    $[168] = t101;
    $[169] = t102;
  } else {
    t102 = $[169];
  }
  let t103;
  if ($[170] !== t100 || $[171] !== t102 || $[172] !== t96 || $[173] !== t98 || $[174] !== t99) {
    t103 = <Container large={true} left={true}>{t96}{t97}{t98}{t99}{t100}{t102}</Container>;
    $[170] = t100;
    $[171] = t102;
    $[172] = t96;
    $[173] = t98;
    $[174] = t99;
    $[175] = t103;
  } else {
    t103 = $[175];
  }
  let t104;
  if ($[176] !== t103 || $[177] !== t62) {
    t104 = <>{t62}{t63}{t103}</>;
    $[176] = t103;
    $[177] = t62;
    $[178] = t104;
  } else {
    t104 = $[178];
  }
  return t104;
};
export default OrganizationBilling;
function _temp(location) {
  return {
    ...location,
    pathname: "/settings/organization/billing/email"
  };
}
function _temp2(location_0) {
  return {
    ...location_0,
    pathname: "/settings/organization/billing/payment-method",
    state: {
      scroll: false
    }
  };
}
function _temp3(location_1) {
  return {
    ...location_1,
    pathname: "/settings/organization/billing/address",
    state: {
      scroll: false
    }
  };
}
function _temp4(location_2) {
  return {
    ...location_2,
    pathname: "/settings/organization/plans",
    state: {
      scroll: false
    }
  };
}
function _temp5(location_3) {
  return {
    ...location_3,
    pathname: "/settings/organization/outpost/plans",
    state: {
      scroll: false
    }
  };
}